import React, { useState, useEffect } from 'react';
import '../styles/components/applicationConfirmModal.scss';
import { useApplicationModal } from '../contexts/ApplicationModalContext';
import { useJobModal } from '../contexts/JobModalContext';
import { addApplication } from '../services/applicationService';
import { toast } from 'react-hot-toast';
import axiosClient from '../axios-client';
import { AxiosError } from 'axios';

const ApplicationConfirmModal: React.FC = () => {
  const { 
    showModal, 
    jobTitle, 
    companyName, 
    jobId, 
    resumeId,
    coverLetterId,
    setModalData, 
    onConfirm,
    resumeContent,
    coverLetterContent
  } = useApplicationModal();
  const { closeJobModalAfterApplication } = useJobModal();
  const [isClosing, setIsClosing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [applicationData, setApplicationData] = useState<any>(null);

  useEffect(() => {
    if (showModal) {
      setIsClosing(false);
      // localStorage removed - data passed through props instead
    }
  }, [showModal, jobId, resumeContent, coverLetterContent]);

  const saveApplicationDocuments = async () => {
    const user = JSON.parse(String(localStorage.getItem("user")));
    
    // localStorage removed - use resume data from props/memory instead
    
    // Use resume content directly from props
    let finalResumeContent;
    try {
      finalResumeContent = resumeContent;

      // Ensure contact information is included
      if (user) {
        finalResumeContent = {
          ...finalResumeContent,
          firstName: finalResumeContent.firstName || user.firstName,
          lastName: finalResumeContent.lastName || user.lastName,
          email: finalResumeContent.email || user.email,
          phone: finalResumeContent.phone || user.phone,
          linkedin: finalResumeContent.linkedin || user.linkedin,
          website: finalResumeContent.website || user.website,
          location: finalResumeContent.location || user.location,
          citizenship: finalResumeContent.citizenship || user.citizenship,
        };
      }
    } catch (error) {
      console.error('Error processing resume content:', error);
      finalResumeContent = resumeContent;
    }

    // Use cover letter content directly from props
    const finalCoverLetterContent = coverLetterContent;

    // Format cover letter content to preserve paragraphs
    const formattedCoverLetter = finalCoverLetterContent
      ? finalCoverLetterContent
          .split(/\n\s*\n/)  // Split into paragraphs
          .map((paragraph: string) => paragraph.trim())  // Trim each paragraph
          .join('\n\n')      // Join with double newlines
      : null;
    
    const finalJobId = jobId;
    


    if (!user?._id) {
      console.error('No user ID available');
      toast.error('User ID is required');
      return false;
    }

    if (!finalJobId) {
      console.error('No job ID available');
      toast.error('Job ID is required');
      return false;
    }

    if (!finalResumeContent || typeof finalResumeContent !== 'object') {
      console.error('Invalid resume content:', finalResumeContent);
      toast.error('Valid resume content is required');
      return false;
    }

    try {
      // Prepare the request data
      const requestData = {
        jobId: finalJobId,
        userId: user._id,
        resume: finalResumeContent,
        coverLetter: formattedCoverLetter || null
      };



      const response = await axiosClient.post('/application-documents', requestData);

      if (response.status === 200) {
        toast.success('Application documents saved successfully');
        // localStorage cleanup removed - no local storage used
        return true;
      } else {
        console.error('Unexpected response status:', response.status);
        toast.error('Failed to save application documents');
        return false;
      }
    } catch (error) {
      console.error('Error saving application documents:', error);
      if (error instanceof AxiosError && error.response) {
        console.error('Error response:', error.response.data);
        toast.error(error.response.data.message || 'Failed to save application documents');
      } else {
        toast.error('Failed to save application documents');
      }
      return false;
    }
  };

  const handleClose = async (didApply: boolean) => {
    if (didApply && jobId) {
      setIsSubmitting(true);
      try {
        // First save the documents
        const documentsSaved = await saveApplicationDocuments();
        if (!documentsSaved) {
          toast.error('Failed to save application documents');
          return;
        }

        // Then track the application
        await addApplication(jobId);
        
        toast.success('Application tracked successfully!');
        window.dispatchEvent(new Event('application-updated'));
        
        // Call onConfirm to notify that user has applied
        // This will trigger the JobDetailsModal to close through JobModalContext
        onConfirm(true);
        
        // Actually close the job modal
        closeJobModalAfterApplication();
      } catch (error) {
        console.error('Error processing application:', error);
        toast.error('Failed to track application. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // User clicked "No", don't close the job modal
      onConfirm(false);
    }

    setIsClosing(true);
    setTimeout(() => {
      setModalData({ showModal: false });
    }, 200);
  };

  if (!showModal) return null;

  return (
    <div className={`application-confirm-backdrop ${isClosing ? 'closing' : ''}`}>
      <div className="application-confirm-content">
        <h2>Did you apply?</h2>
        <p>If you applied, we'll add it to your application tracker.</p>
        <div className="button-group">
          <button 
            className="keep-button" 
            onClick={() => handleClose(false)}
            disabled={isSubmitting}
          >
            No
          </button>
          <button 
            className="discard-button" 
            onClick={() => handleClose(true)}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Tracking...' : 'Yes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApplicationConfirmModal; 